/**
 * UIBook.jsx
 *
 * Living style reference for the AE Tracker badge/pill design system.
 * SuperAdmin-only (internal design reference). Examples are hardcoded against
 * the canonical CSS classes so this page stays a stable source of truth,
 * independent of the editable admin config.
 *
 * The system has four pill families, each visually distinct on purpose:
 *   - Severity  → SOLID warm ramp (filled)
 *   - Status    → SOFT cool blue→teal ramp (tinted) + leading dot
 *   - Event     → neutral DASHED tag (no fill hue)
 *   - Cost      → GREY fill, coloured outline + text, NO dot
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { CostCategoryPill } from '../components/CostChip.jsx';
import Avatar from '../components/Avatar.jsx';

function Badge({ cssClass, label, dot = true }) {
  return (
    <span className={`badge ${cssClass}`}>
      {dot && <span className="dot" />}
      {label}
    </span>
  );
}

function Section({ title, desc, rules, children }) {
  return (
    <div className="panel" style={{ marginBottom: 24 }}>
      <div className="panel-head">
        <div>
          <div className="panel-title">{title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{desc}</div>
        </div>
      </div>
      <div className="panel-body">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: rules ? 14 : 0 }}>
          {children}
        </div>
        {rules && (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-3)', lineHeight: 1.7 }}>
            {rules.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

const STATUS = [
  { cssClass: 'status-new',  label: 'New' },
  { cssClass: 'status-open', label: 'Open' },
  { cssClass: 'status-inv',  label: 'Investigating' },
  { cssClass: 'status-wait', label: 'Waiting on Client' },
  { cssClass: 'status-res',  label: 'Resolved' },
  { cssClass: 'status-esc',  label: 'Escalated' },
];

const SEVERITY = [
  { cssClass: 'sev-1', label: 'Code 1 · Mild' },
  { cssClass: 'sev-2', label: 'Code 2 · Moderate' },
  { cssClass: 'sev-3', label: 'Code 3 · Severe' },
  { cssClass: 'sev-4', label: 'Code 4 · Serious' },
  { cssClass: 'sev-5', label: 'Code 5 · Critical' },
];

const COST = [
  { cssClass: 'cost-refund',       label: 'Refund' },
  { cssClass: 'cost-goodwill',     label: 'Goodwill' },
  { cssClass: 'cost-corrective',   label: 'Corrective treatment' },
  { cssClass: 'cost-medical',      label: 'Medical / GP' },
  { cssClass: 'cost-legal',        label: 'Legal' },
  { cssClass: 'cost-compensation', label: 'Compensation' },
  { cssClass: 'cost-other',        label: 'Other' },
];

export default function UIBook() {
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!user?.canManageAdminConfig) return null;

  return (
    <>
      <header className="page-head">
        <div>
          <div className="breadcrumb">
            <a href="#" onClick={e => { e.preventDefault(); navigate('/dashboard'); }}>Overview</a>
            <span>›</span>
            <span>UI Book</span>
          </div>
          <h1>UI Book</h1>
          <div className="page-sub">Badge &amp; pill design system — four families, each distinct at a glance.</div>
        </div>
      </header>

      <Section
        title="Status badges"
        desc="Workflow state. Soft, cool blue→teal ramp — cases warm toward brand teal as they near resolution."
        rules={[
          'Tinted fill + 1px soft border + a leading 6px dot (dot colour is a saturated step on the ramp, distinct from the text).',
          'New is emphasised: 1.5px solid border + a focus-style ring, so the entry state stands out.',
          'Escalated is an off-path exception and stays red.',
        ]}
      >
        {STATUS.map(s => <Badge key={s.cssClass} {...s} />)}
      </Section>

      <Section
        title="Severity badges"
        desc="Clinical escalation. Solid, filled warm ramp (yellow → maroon) — the only filled, warm family."
        rules={[
          'Solid saturated fill with contrast-adjusted text — visually heavier than the soft status badges.',
          'No green in the ramp, so it never collides with the cool status hues.',
        ]}
      >
        {SEVERITY.map(s => <Badge key={s.cssClass} {...s} />)}
      </Section>

      <Section
        title="Event-type tags"
        desc="Shown in the Event column for non-treatment cases (where a treatment badge would be)."
        rules={[
          'Neutral grey with a dashed border — marks it as a type tag, not a treatment value or a status.',
          'No hue, so it stays clear of the severity (warm) and status (cool) colour systems.',
        ]}
      >
        <Badge cssClass="event-type" label="Near miss" dot={false} />
        <Badge cssClass="event-type" label="Clinic incident" dot={false} />
      </Section>

      <Section
        title="Cost-category pills"
        desc="Labels a cost line-item (refund, goodwill, …) on a case's Costs tab."
        rules={[
          'Background is always grey #F1F3F6 — never tinted with the category hue. The grey fill is what separates cost pills from the tinted status badges.',
          'The hue lives only in the border + text. No leading dot (status badges have one; cost pills must not).',
          'New categories get a new dark-ink / mid-border hue pair at similar contrast — never reuse a status colour for a same-screen cost pill.',
        ]}
      >
        {COST.map(c => <CostCategoryPill key={c.cssClass} cssClass={c.cssClass} label={c.label} />)}
      </Section>

      <Section
        title="User avatar"
        desc="The signed-in user, shown in the sidebar and on lock/identity surfaces."
        rules={[
          'Always round. Pulls the Microsoft profile photo from Graph on login (user.photoUrl).',
          'No photo → initials on neutral grey #E6E8EC with brand-teal #0D9488 text.',
          'Collapsed sidebar: 36px to match the logo mark; expanded: 32px next to the name.',
        ]}
      >
        <Avatar initials="MC" size={36} />
        <Avatar initials="LS" size={32} />
      </Section>

      <Section
        title="Charts ↔ badges are linked"
        desc="Chart colours are not hardcoded — they read each config item's cssClass."
        rules={[
          'Severity, status and cost-category chart colours come from badgeColors.classColor(cssClass) — the same class that styles the badge/pill.',
          'Editing a category/code/status swatch in the admin config reaches the dashboard charts automatically; the two can never drift.',
        ]}
      >
        <span className="badge sev-3"><span className="dot" />Code 3</span>
        <span className="badge status-open"><span className="dot" />Open</span>
        <CostCategoryPill cssClass="cost-refund" label="Refund" />
      </Section>
    </>
  );
}
